// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

/**
 * Where a press landed inside the element that handled it.
 *
 * React Native and react-native-web hand `onPress` two different animals, and the
 * difference is invisible until you try to read a coordinate off it:
 *
 * - On native, `nativeEvent` is a touch event carrying `locationX`/`locationY`, already
 *   relative to the element.
 * - On web, `onPress` is wired to the DOM `click` event, so `nativeEvent` is a plain
 *   `MouseEvent`. It has `clientX`/`clientY` in viewport space and no `locationX` at all —
 *   react-native-web says so itself in PressResponder's `onClick` comment.
 *
 * So the web branch subtracts the element's own rect. Returns null when neither form is
 * usable — a keyboard activation of the same control, for instance, has no coordinates,
 * and inventing one would drop a marker somewhere the player never pointed.
 */

export type PressPointEvent = {
  locationX?: number | null;
  locationY?: number | null;
  clientX?: number | null;
  clientY?: number | null;
};

/** Anything that can report its own position on screen. The DOM node, on web. */
export type MeasurableElement = {
  getBoundingClientRect?: () => { left: number; top: number };
} | null;

const finite = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const pressPointInElement = (
  nativeEvent: PressPointEvent | null | undefined,
  element: MeasurableElement
): { x: number; y: number } | null => {
  if (!nativeEvent) return null;

  const { locationX, locationY } = nativeEvent;
  if (finite(locationX) && finite(locationY)) {
    return { x: locationX, y: locationY };
  }

  const { clientX, clientY } = nativeEvent;
  if (!finite(clientX) || !finite(clientY)) return null;

  const rect = element?.getBoundingClientRect?.();
  if (!rect || !finite(rect.left) || !finite(rect.top)) return null;

  return { x: clientX - rect.left, y: clientY - rect.top };
};
